import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {PriceHistoryRequest} from "../../../models/PriceHistoryRequest";
import {PlotlyService} from "../../../services/plotly.service";
import {SchwabAuthService} from "../../../services/schwab-auth.service";
import {SchwabDataService} from "../../../services/schwab-data.service";
import { OptionChainAnalyzer, OptionChain, OptionContract, OptionDeliverable} from "../../../models/ChainModels";
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { calculateInterdayProbabilityCurve } from '../../../shared/Functions/CalculateInterdayProbabilityCurve'
import { calculateInterdayProbabilityCurveNoSkip } from '../../../shared/Functions/CalculateInterdayProbabilityCurveNoSkip'
import {MessageService} from "primeng/api";
import {HttpErrorResponse} from "@angular/common/http";
import {partialIntegral} from "../../../shared/Functions/PartialIntegral";
import {integralSplitPoint} from "../../../shared/Functions/IntegralSplitPoint";
import {OptionChainRequest} from "../../../models/OptionChainRequest";

@Component({
	selector: 'app-max-pain-surface',
	templateUrl: './max-pain-surface.component.html',
	styleUrl: './max-pain-surface.component.scss'
})
export class MaxPainSurfaceComponent implements OnInit {
	constructor(private plot: PlotlyService,
				private cdRef: ChangeDetectorRef,
				private schwabAuth: SchwabAuthService,
				private schwabData: SchwabDataService,
				private fb: FormBuilder,
				private messageService: MessageService) {
		this.optionChainForm = this.fb.group({
			symbol: ['SPY', Validators.required],
		});
		this.dataDisplayForm = this.fb.group({
			binSize: [0.1, Validators.required],
			testLevel: [4],
			splitPointPercentage: [95],
			showRawData: [false],
		})
	}

	optionChainForm: FormGroup;
	dataDisplayForm: FormGroup;


	periodTypes: string[] = ['day', 'month', 'year', 'ytd'];
	frequencyTypes: string[] = ['minute', 'daily', 'weekly', 'monthly'];
	binSizes: number[] = [0.01, 0.1, 1, 2];

	OptionChainData: OptionChain | undefined;
	analyzer: OptionChainAnalyzer | undefined;

	showRawDataBool: boolean = false;

	splitProb: { above: number; below: number } = { above: 0, below: 0 };
	splitPoint: string = '';

	title: string = 'Max Pain by DTE';
	graphData: any;
	graphLayout: any;

	ngOnInit() {
		// (no-op)
	}

	private getStorageKey(symbol: string): string {
		return `maxPainSnapshots:${symbol.trim().toUpperCase()}`;
	}

	private persistSnapshot(symbol: string, points: { dte: number; strike: number; pain: number }[], underlying: number): void {
		try {
			const key = this.getStorageKey(symbol);
			const existing: any[] = JSON.parse(localStorage.getItem(key) || '[]');
			const snapshot = {
				ts: Date.now(),
				underlying,
				points
			};
			existing.push(snapshot);
			localStorage.setItem(key, JSON.stringify(existing));
		} catch {
			// ignore storage errors silently
		}
	}

	private loadSnapshots(symbol: string): { ts: number; underlying: number; points: { dte: number; strike: number; pain: number }[] }[] {
		try {
			const key = this.getStorageKey(symbol);
			const arr = JSON.parse(localStorage.getItem(key) || '[]');
			if (Array.isArray(arr)) return arr;
			return [];
		} catch {
			return [];
		}
	}

	plotHistory3D(): void {
		const symbol = (this.optionChainForm.get('symbol')?.value as string || '').trim().toUpperCase();
		if (!symbol) {
			this.showErrorToast('Enter a symbol first.');
			return;
		}

		const snapshots = this.loadSnapshots(symbol);
		if (!snapshots.length) {
			this.showErrorToast('No saved history found for this symbol.');
			return;
		}

		// Build union of all DTEs (x-axis)
		const dteSet = new Set<number>();
		for (const s of snapshots) {
			for (const p of s.points) {
				if (Number.isFinite(p.dte)) dteSet.add(p.dte);
			}
		}
		const x = Array.from(dteSet).sort((a, b) => a - b); // DTE ascending

		// Sort snapshots by timestamp (y-axis order)
		snapshots.sort((a, b) => a.ts - b.ts);

		// y axis will be numeric indices; we'll provide tick labels as dates
		const y = snapshots.map((_, i) => i);

		// Build Z matrix (rows = snapshots in time, cols = DTEs) -> value = strike
		const z: (number | null)[][] = snapshots.map(s => {
			const row = new Array<number | null>(x.length).fill(null);
			const strikeByDte = new Map<number, number>();
			for (const p of s.points) {
				strikeByDte.set(p.dte, p.strike);
			}
			for (let j = 0; j < x.length; j++) {
				const strike = strikeByDte.get(x[j]);
				row[j] = Number.isFinite(strike as number) ? (strike as number) : null;
			}
			return row;
		});

		this.graphData = [
			{
				type: 'surface',
				x,
				y,
				z,
				colorscale: 'Viridis',
				showscale: true,
				name: 'Max Pain Strike Surface',
				hovertemplate: 'DTE=%{x}<br>Snapshot=%{y}<br>Max Pain Strike=%{z}<extra></extra>'
			}
		];

		// Build tick labels for y from timestamps
		const yTickvals = y;
		const yTicktext = snapshots.map(s => {
			const d = new Date(s.ts);
			return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
		});

		this.graphLayout = {
			title: `Max Pain History (3D) - ${symbol}`,
			scene: {
				xaxis: { title: 'Days to Expiration (DTE)' },
				yaxis: { title: 'Snapshot Time', tickmode: 'array', tickvals: yTickvals, ticktext: yTicktext },
				zaxis: { title: 'Max Pain Strike' }
			},
			margin: { l: 0, r: 0, b: 0, t: 40 }
		};
	}

	clearHistory(): void {
		const symbol = (this.optionChainForm.get('symbol')?.value as string || '').trim().toUpperCase();
		if (!symbol) {
			this.showErrorToast('Enter a symbol first.');
			return;
		}
		try {
			localStorage.removeItem(this.getStorageKey(symbol));
			this.showErrorToast('History cleared.');
		} catch {
			// ignore
		}
	}

	submitForm() {
		if (this.optionChainForm.valid) {
			const requestParams: OptionChainRequest = this.optionChainForm.value;
			requestParams.toDate = this.getDate30DaysFromToday();
			this.schwabData.getOptionChain(requestParams).subscribe(
				(response) => {
					this.OptionChainData = response;
					this.analyzer = new OptionChainAnalyzer(this.OptionChainData);
					const maxPainMap = this.analyzer.getMaxPainByExpiration();

					if (!maxPainMap || Object.keys(maxPainMap).length === 0) {
						this.showErrorToast('No valid data to calculate Max Pain.');
						return;
					}

					// Build data points: x = DTE (days), y = max pain strike
					const points: { dte: number; strike: number; exp: string; pain: number }[] = [];
					const today = new Date();

					for (const [expStr, { strike, pain }] of Object.entries(maxPainMap)) {
						const expDate = new Date(expStr);
						if (isNaN(expDate.getTime())) continue;
						const dte = Math.max(0, Math.round((expDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
						if (isFinite(dte) && isFinite(strike)) {
							points.push({ dte, strike, exp: expStr, pain });
						}
					}

					// Sort by DTE
					points.sort((a, b) => a.dte - b.dte);

					if (points.length === 0) {
						this.showErrorToast('No valid expirations to plot.');
						return;
					}

					const x = points.map(p => p.dte);
					const y = points.map(p => p.strike);
					const hover = points.map(p => `Exp: ${p.exp}<br>DTE: ${p.dte}<br>Max Pain Strike: ${p.strike}<br>Total Pain: ${p.pain}`);

					// Add a horizontal line for the current underlying price across the DTE range
					const underlying = Number(this.OptionChainData?.underlyingPrice);
					const underlyingTrace = Number.isFinite(underlying) && x.length > 0
						? {
							x: [x[0], x[x.length - 1]],
							y: [underlying, underlying],
							type: 'scatter',
							mode: 'lines',
							name: 'Underlying Price',
							line: { color: 'crimson', dash: 'dash' as const },
							hovertemplate: 'Underlying=%{y}<extra></extra>'
						}
						: null;

					this.graphData = [
						{
							x,
							y,
							type: 'scatter',
							mode: 'lines+markers',
							name: 'Max Pain Strike',
							hovertemplate: '%{text}<extra></extra>',
							text: hover
						},
						...(underlyingTrace ? [underlyingTrace] : [])
					];

					this.graphLayout = {
						title: this.title,
						xaxis: { title: 'Days to Expiration (DTE)' },
						yaxis: { title: 'Max Pain Strike' },
						margin: { l: 50, r: 20, t: 40, b: 50 },
						showlegend: true
					};

					// Persist this snapshot for 3D history
					this.persistSnapshot(
						(this.optionChainForm.get('symbol')?.value as string) || '',
						points.map(({ dte, strike, pain }) => ({ dte, strike, pain })),
						underlying
					);
				},
				(error: HttpErrorResponse) => {
					console.error('Error getting option chain', error);
					const detail = (error?.error?.errors?.[0]?.detail) ?? error.message ?? 'Unknown error';
					this.showErrorToast(detail);
				}
			)
		} else {
			this.showErrorToast('Form is Invalid');
		}
	}
	getDate30DaysFromToday(): string {
		const today = new Date();
		const futureDate = new Date(today);
		futureDate.setDate(today.getDate() + 30);

		const year = futureDate.getFullYear();
		const month = String(futureDate.getMonth() + 1).padStart(2, '0');
		const day = String(futureDate.getDate()).padStart(2, '0');

		return `${year}-${month}-${day}`;
	}

	resetForm() {
		this.optionChainForm.reset();
	}

	onRawDataChange(value: boolean): void {
		this.showRawDataBool = value;
	}

	showErrorToast(msg: string) {
		this.messageService.add({ severity: 'error', summary: 'Error', detail: msg, key: 'br', life: 10000 });
	}

	totalProb: number = 0;

}
